/* BEATSEEK — "On the Build List" board (desktop only ≥1100px).
 * Emerald slide-out rail on the LEFT edge of the Wall — mirror of the right-side
 * Ideas/Emotes rails. Shows community-voted ideas moving through the pipeline:
 * 🏆 Voted → 🔨 In Progress → 🚀 Shipped.
 * Exports useBuildList + BuildListCard for the mobile Ideas modal tab. */
import React, { useEffect, useState, useCallback } from 'react';
import { Hammer, ChevronRight, ChevronLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_OPEN = 'beatseek_buildlist_panel_open';

export const useBuildList = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/build-list`);
      if (r.ok) {
        const d = await r.json();
        setItems(d.items || []);
      }
    } catch (e) { /* noop */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  return { items, loading, reload: load };
};

const STAGES = [
  { key: 'voted', label: 'Voted', icon: '🏆', dateKey: 'voted_at' },
  { key: 'in_progress', label: 'In Progress', icon: '🔨', dateKey: 'in_progress_at' },
  { key: 'shipped', label: 'Shipped', icon: '🚀', dateKey: 'shipped_at' },
];
const STAGE_IDX = { voted: 0, in_progress: 1, shipped: 2 };

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

export const BuildListCard = ({ item }) => {
  const activeIdx = STAGE_IDX[item.status] ?? 0;
  const isShipped = item.status === 'shipped';
  return (
    <div
      data-testid={`build-list-item-${item.poll_id}`}
      style={{
        padding: '10px 11px',
        background: isShipped ? 'rgba(52,211,153,0.06)' : 'rgba(255,255,255,0.03)',
        border: `1px solid ${isShipped ? 'rgba(52,211,153,0.45)' : 'rgba(52,211,153,0.2)'}`,
        borderRadius: '11px',
      }}
    >
      <p style={{ color: '#e6e9f2', fontSize: '0.78rem', fontWeight: 600, lineHeight: 1.45, margin: '0 0 7px' }}>
        {item.text}
      </p>
      <div style={{ marginBottom: '9px' }}>
        <span style={{
          fontSize: '0.62rem', fontWeight: 800, color: '#34d399',
          background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.3)',
          borderRadius: '6px', padding: '2px 7px', letterSpacing: '0.04em',
        }}>
          🔥 {item.build_pct}% Build · {item.total_votes} vote{item.total_votes === 1 ? '' : 's'}
        </span>
      </div>

      {/* Pipeline */}
      <div style={{ display: 'flex', alignItems: 'flex-start' }} data-testid={`build-pipeline-${item.poll_id}`}>
        {STAGES.map((st, i) => {
          const done = i <= activeIdx;
          const isActive = i === activeIdx;
          return (
            <React.Fragment key={st.key}>
              {i > 0 && (
                <div style={{
                  flex: 1, height: '2px', marginTop: '7px',
                  background: i <= activeIdx ? 'linear-gradient(90deg, #34d399, #059669)' : 'rgba(255,255,255,0.1)',
                  minWidth: '10px',
                }} />
              )}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', flexShrink: 0 }}>
                <div style={{
                  width: '16px', height: '16px', borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '8px',
                  background: done ? 'rgba(52,211,153,0.18)' : 'rgba(255,255,255,0.05)',
                  border: `1.5px solid ${done ? '#34d399' : 'rgba(255,255,255,0.15)'}`,
                  boxShadow: isActive ? '0 0 8px rgba(52,211,153,0.5)' : 'none',
                }}>
                  {done ? st.icon : ''}
                </div>
                <span style={{
                  fontSize: '0.52rem', fontWeight: isActive ? 800 : 600, letterSpacing: '0.03em',
                  color: done ? '#34d399' : '#64748b', textTransform: 'uppercase', whiteSpace: 'nowrap',
                }}>
                  {st.label}
                </span>
                {item[st.dateKey] && done && (
                  <span style={{ fontSize: '0.5rem', color: '#64748b' }}>{fmtDate(item[st.dateKey])}</span>
                )}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export const BuildListEmpty = () => (
  <div style={{ padding: '14px', textAlign: 'center', fontSize: '11px', color: '#475569',
                border: '1px dashed rgba(52,211,153,0.2)', borderRadius: '10px' }}>
    <div style={{ marginBottom: '4px' }}>Nothing on the build list yet.</div>
    <div>Vote 🔥 Build it on community ideas to put them here!</div>
  </div>
);

const BuildListPanel = () => {
  const { items, loading } = useBuildList();
  const [viewport, setViewport] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [open, setOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(STORAGE_OPEN) === '1';
  });

  useEffect(() => {
    if (typeof window !== 'undefined') localStorage.setItem(STORAGE_OPEN, open ? '1' : '0');
  }, [open]);

  useEffect(() => {
    const onResize = () => setViewport(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (viewport < 1100) return null;

  const PANEL_WIDTH = 250;
  // Right-side rail (bottom slot) — anchored via `left` so the panel tucks behind the Wall's RIGHT edge
  const left = open ? `calc(50% + 336px)` : `calc(50% + 356px)`;

  return (
    <div
      data-testid="build-list-panel"
      style={{
        position: 'fixed',
        bottom: '5vh',
        left,
        transition: 'left 0.35s cubic-bezier(0.22, 1, 0.36, 1)',
        display: 'flex',
        flexDirection: 'row-reverse',
        alignItems: 'stretch',
      }}
    >
      {/* Toggle handle (right side of the panel) */}
      <button
        data-testid="build-list-toggle"
        onClick={() => setOpen(o => !o)}
        title={open ? 'Hide build list' : 'Show the community build list'}
        style={{
          width: '24px',
          background: 'linear-gradient(180deg, rgba(52,211,153,0.16), rgba(5,150,105,0.16))',
          border: '1px solid rgba(52,211,153,0.38)',
          borderTopRightRadius: '12px',
          borderBottomRightRadius: '12px',
          borderTopLeftRadius: open ? '0' : '12px',
          borderBottomLeftRadius: open ? '0' : '12px',
          color: '#34d399',
          cursor: 'pointer',
          padding: '14px 0',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
          minHeight: '110px',
          boxShadow: open ? '0 8px 22px rgba(0,0,0,0.45), 0 0 14px 3px rgba(52,211,153,0.4)' : '0 8px 22px rgba(0,0,0,0.45)',
          transition: 'transform 0.2s cubic-bezier(0.22,1,0.36,1), box-shadow 0.2s, border-color 0.2s',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'translateX(4px)'; e.currentTarget.style.boxShadow = '0 12px 28px rgba(0,0,0,0.5), 0 0 20px 5px rgba(52,211,153,0.6)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = open ? '0 8px 22px rgba(0,0,0,0.45), 0 0 14px 3px rgba(52,211,153,0.4)' : '0 8px 22px rgba(0,0,0,0.45)'; }}
      >
        {open ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        {!open && (
          <div style={{
            writingMode: 'vertical-rl',
            transform: 'rotate(180deg)',
            fontSize: '9px', fontWeight: 800, letterSpacing: '0.18em',
            textTransform: 'uppercase',
            background: 'linear-gradient(135deg, #34d399, #059669)',
            WebkitBackgroundClip: 'text', backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            Build · List
          </div>
        )}
        {!open && <Hammer size={12} style={{ color: '#34d399' }} />}
      </button>

      {/* Panel */}
      <div
        data-testid="build-list-body"
        style={{
          width: open ? `${PANEL_WIDTH}px` : '0px',
          opacity: open ? 1 : 0,
          overflow: 'hidden',
          maxHeight: open ? '28vh' : '0px',
          overflowY: 'auto',
          background: 'linear-gradient(180deg, #0d0d14 0%, #07070b 100%)',
          borderTop: open ? '1px solid rgba(52,211,153,0.28)' : '1px solid transparent',
          borderLeft: open ? '1px solid rgba(52,211,153,0.28)' : '1px solid transparent',
          borderBottom: open ? '1px solid rgba(52,211,153,0.28)' : '1px solid transparent',
          borderRight: 'none',
          borderTopLeftRadius: '14px',
          borderBottomLeftRadius: '14px',
          padding: open ? '14px 14px 14px 28px' : '0',
          boxShadow: open ? '-10px 0 24px rgba(0,0,0,0.45), 0 0 0 1px rgba(52,211,153,0.1)' : 'none',
          transition: 'width 0.35s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.25s ease, padding 0.35s',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', justifyContent: 'flex-end' }}>
          <Hammer size={14} style={{ color: '#34d399' }} />
          <div style={{
            fontSize: '10px', fontWeight: 800, letterSpacing: '0.16em',
            background: 'linear-gradient(135deg, #34d399, #059669)',
            WebkitBackgroundClip: 'text', backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textTransform: 'uppercase',
          }}>
            Build List & Status
          </div>
        </div>
        <div style={{ fontSize: '9px', color: '#64748b', marginBottom: '12px', letterSpacing: '0.06em', textAlign: 'right' }}>
          You voted — watch it get built
        </div>

        {loading ? (
          <div style={{ padding: '20px 0', textAlign: 'center', color: '#475569', fontSize: '11px' }}>Loading…</div>
        ) : items.length === 0 ? (
          <BuildListEmpty />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {items.map(it => <BuildListCard key={it.poll_id} item={it} />)}
          </div>
        )}
      </div>
    </div>
  );
};

export default BuildListPanel;
